// Shared client-side Radio context for roles that may choose among authorized
// sites. This is navigation state only: database functions and RLS remain the
// authorization boundary for resolving channels and starting sessions.
import type { RadioSite } from "@/hooks/useRadioContext";

const STORAGE_PREFIX = "missionready.radioSite";
const CHANGE_EVENT = "missionready:radio-site-change";
const SELECTOR_ROLES = new Set(["org_owner", "admin", "dispatcher", "supervisor"]);

function storageKey(orgId: string) {
  return `${STORAGE_PREFIX}:${orgId}`;
}

export function canSelectRadioSite(
  role: string | null | undefined,
  isPlatformAdmin = false,
): boolean {
  return isPlatformAdmin || (!!role && SELECTOR_ROLES.has(role));
}

export function getSelectedRadioSiteId(orgId: string): string | null {
  if (!orgId || typeof window === "undefined") return null;
  return window.localStorage.getItem(storageKey(orgId));
}

export function setSelectedRadioSiteId(orgId: string, siteId: string | null): void {
  if (!orgId || typeof window === "undefined") return;
  const key = storageKey(orgId);
  if (siteId) window.localStorage.setItem(key, siteId);
  else window.localStorage.removeItem(key);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { orgId, siteId } }));
}

export function subscribeToRadioSiteSelection(
  orgId: string,
  onChange: (siteId: string | null) => void,
): () => void {
  if (!orgId || typeof window === "undefined") return () => undefined;
  const onCustom = (event: Event) => {
    const detail = (event as CustomEvent<{ orgId?: string; siteId?: string | null }>).detail;
    if (detail?.orgId === orgId) onChange(detail.siteId ?? null);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === storageKey(orgId)) onChange(event.newValue);
  };
  window.addEventListener(CHANGE_EVENT, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

export function resolveSelectableRadioSite(
  selectedSiteId: string | null,
  authorizedSites: RadioSite[],
  punchedSiteId: string | null = null,
): RadioSite | null {
  for (const candidate of [selectedSiteId, punchedSiteId]) {
    if (!candidate) continue;
    const match = authorizedSites.find((site) => site.id === candidate);
    if (match) return match;
  }
  // Owners, admins, dispatchers and supervisors are authorized everywhere they
  // can read, so a hold never has to fail for want of a manual pick: the first
  // authorized site is used and its name is always shown while transmitting.
  return authorizedSites[0] ?? null;
}